import { useState, type ReactNode } from "react";

import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { Button } from "@/components/ui/Button";
import { SideNav, type SideNavItem } from "@/components/ui/SideNav";
import { cn } from "@/lib/classNames";

const SAMPLE_TABS = [
  { id: "overview", label: "Overview" },
  { id: "encounters", label: "Encounters" },
  { id: "loot", label: "Loot" },
] as const;

const SAMPLE_NAV_ITEMS: SideNavItem[] = [
  { href: "#", label: "Dashboard", match: "exact" },
  { href: "#campaigns", label: "Campaigns", match: "prefix" },
  { href: "#settings", label: "Settings", match: "prefix" },
] as const;

function SectionTitle({ id, children }: { id: string; children: string }) {
  return (
    <h2
      id={id}
      className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.12em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
    >
      {children}
    </h2>
  );
}

function RuleCard({
  title,
  items,
}: {
  title: string;
  items: readonly string[];
}) {
  return (
    <article className="border-[3px] border-neutral-950 bg-[#fffaf1] p-6 shadow-[8px_8px_0_rgba(19,19,19,0.1)]">
      <h3 className="text-lg font-bold tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif]">
        {title}
      </h3>
      <ul className="mt-4 space-y-3 text-sm leading-relaxed text-neutral-800">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </article>
  );
}

function ExampleCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <article className="space-y-4 border-[3px] border-neutral-950 bg-[#fffaf1] p-6 shadow-[8px_8px_0_rgba(19,19,19,0.1)]">
      <div>
        <h3 className="text-lg font-bold tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif]">
          {title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-700">{description}</p>
      </div>
      {children}
    </article>
  );
}

function LowercaseTabsExample() {
  const [activeId, setActiveId] = useState("overview");

  return (
    <div className="overflow-hidden border-[3px] border-neutral-950">
      <div className="flex border-b-[3px] border-neutral-950">
        {SAMPLE_TABS.map((tab, index) => {
          const active = tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveId(tab.id)}
              className={cn(
                "relative flex-1 px-4 py-3 text-sm font-semibold tracking-normal transition-colors",
                index > 0 && "border-l-[3px] border-neutral-950",
                active
                  ? "bg-neutral-950/6 text-neutral-950 shadow-[inset_0_2px_6px_rgba(0,0,0,0.12)]"
                  : "bg-[#fffaf1] text-neutral-600 hover:bg-neutral-950/3 hover:text-neutral-800",
              )}
            >
              <span
                className={cn(
                  "inline-block border-b-2",
                  active ? "border-neutral-950 pb-0.5" : "border-transparent pb-0.5",
                )}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
      <div className="bg-[#fffaf1] p-4 text-sm text-neutral-800">
        Content for{' '}
        <strong className="font-semibold text-neutral-950">
          {SAMPLE_TABS.find((tab) => tab.id === activeId)?.label}
        </strong>
        .
      </div>
    </div>
  );
}

function DashboardHeroExample() {
  return (
    <div
      className="overflow-hidden border-[3px] border-neutral-950 p-6 text-[#fff7ec] shadow-[8px_8px_0_rgba(19,19,19,0.14)]"
      style={{
        backgroundImage:
          "linear-gradient(135deg, #5d2f26 0%, #9c4c32 46%, #f0c26f 100%)",
      }}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#fff7ec]/80">
        Dashboard hero
      </p>
      <h3 className="mt-3 text-3xl font-black uppercase tracking-[-0.02em] [font-family:var(--color-lab-display),system-ui,sans-serif]">
        Ember March
      </h3>
      <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#fff7ec]/90">
        Hero and poster surfaces can keep a more stylized uppercase treatment when the text is acting as display chrome.
      </p>
    </div>
  );
}

export function CasingComparisonClient() {
  return (
    <div className="mx-auto min-h-full max-w-5xl px-5 py-12 pb-24 text-neutral-950 sm:px-8">
      <header className="border-b-[3px] border-neutral-950 pb-10">
        <p className="text-sm uppercase tracking-[0.12em] text-neutral-600">
          Design system · Casing rule
        </p>
        <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif] sm:text-5xl">
          Uppercase usage rule
        </h1>
        <p className="mt-4 max-w-3xl text-lg leading-relaxed text-neutral-800">
          Uppercase is an accent treatment for selected chrome, not the default voice of the application.
          Use it for labels, utility controls, section framing, and a few dashboard hero moments.
          Keep names, values, and descriptive content in regular case by default.
        </p>
      </header>

      <div className="mt-14 space-y-20">
        <section className="space-y-6" aria-labelledby="casing-core-rule">
          <SectionTitle id="casing-core-rule">Core rule</SectionTitle>
          <p className="max-w-3xl text-neutral-800">
            The main distinction is between <strong className="font-semibold">chrome</strong> and{' '}
            <strong className="font-semibold">content</strong>. Chrome can carry a stylized uppercase treatment.
            Content such as names, values, and body copy should stay readable and natural.
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            <RuleCard
              title="Use uppercase"
              items={[
                "Micro labels and field labels like Name, Hook, and Visibility",
                "Utility actions like Edit, Delete, and Back to map",
                "Section framing like Details and GM Notes",
                "Side navigation",
                "Buttons by default",
                "Selected dashboard and hero treatments",
              ]}
            />
            <RuleCard
              title="Use regular case"
              items={[
                "Entity names like POI names, campaign names, note titles, and player-facing names",
                "Main descriptive content and values",
                "Tabs by default",
                "Most interior page titles unless they are intentionally decorative",
                "Any UI where readability matters more than surface styling",
              ]}
            />
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="casing-primitives">
          <SectionTitle id="casing-primitives">Primitive defaults</SectionTitle>
          <div className="border-[3px] border-neutral-950 bg-[#fffaf1] p-6 shadow-[8px_8px_0_rgba(19,19,19,0.1)]">
            <ul className="space-y-4 text-sm leading-relaxed text-neutral-800">
              <li>
                <strong className="font-semibold text-neutral-950">Buttons:</strong> uppercase by default
              </li>
              <li>
                <strong className="font-semibold text-neutral-950">Tabs:</strong> regular case by default
              </li>
              <li>
                <strong className="font-semibold text-neutral-950">Side navigation:</strong> uppercase by default
              </li>
              <li>
                <strong className="font-semibold text-neutral-950">Tracking guidance:</strong> when uppercase remains, prefer calmer tracking than the older stamped treatment.
              </li>
            </ul>
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="casing-examples">
          <SectionTitle id="casing-examples">Examples</SectionTitle>
          <p className="max-w-3xl text-neutral-800">
            These examples show the approved combinations in context so the rule can be referenced during future UI work.
          </p>

          <div className="grid gap-6 lg:grid-cols-2">
            <ExampleCard
              title="Label plus value"
              description="Labels stay uppercase with tighter tracking, while names and values stay in regular case."
            >
              <div className={cn("rounded-[24px] p-6", MONOLITH_BOARD_STYLE.panelClassName)}>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-600">Name</p>
                <h4 className="mt-2 text-2xl font-black tracking-tight text-neutral-950">
                  The Hollow Spire
                </h4>
                <div className="mt-5 border-t-2 border-neutral-950/12 pt-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-600">Hook</p>
                  <p className="mt-2 text-base leading-relaxed text-neutral-700">
                    Strange lights flicker from the upper windows each full moon.
                  </p>
                </div>
              </div>
            </ExampleCard>

            <ExampleCard
              title="Buttons stay uppercase"
              description="Shared buttons keep the uppercase treatment because they function as chrome rather than content."
            >
              <div className="flex flex-wrap gap-3">
                <Button variant="primary" chrome="frame" type="button">
                  Save changes
                </Button>
                <Button variant="cancel" chrome="compact" type="button">
                  Cancel
                </Button>
              </div>
            </ExampleCard>

            <ExampleCard
              title="Tabs use regular case"
              description="Tabs should read like readable navigation labels instead of engraved stamps."
            >
              <LowercaseTabsExample />
            </ExampleCard>

            <ExampleCard
              title="Side navigation stays uppercase"
              description="Side navigation can keep the more stylized uppercase treatment because it functions as surface chrome."
            >
              <div className="max-w-xs">
                <SideNav
                  items={SAMPLE_NAV_ITEMS}
                  pathname="#"
                  label="Casing rule side navigation example"
                />
              </div>
            </ExampleCard>

            <ExampleCard
              title="Dashboard hero exception"
              description="A hero or poster surface can keep uppercase for emphasis, even when names stay regular on detail pages."
            >
              <DashboardHeroExample />
            </ExampleCard>

            <ExampleCard
              title="Uppercase tracking guidance"
              description="When uppercase is used, keep the spacing present but controlled. The goal is accent, not noise."
            >
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-600">
                  Preferred micro label
                </p>
                <p className="text-sm leading-relaxed text-neutral-700">
                  Use restrained spacing for uppercase labels, utility controls, and hero treatments instead of the older stamped look.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" chrome="compact" type="button">
                    Save notes
                  </Button>
                  <Button variant="cancel" chrome="compact" type="button">
                    Back to map
                  </Button>
                </div>
              </div>
            </ExampleCard>
          </div>
        </section>
      </div>
    </div>
  );
}
